import React from "react";
import Grid from "@mui/material/Grid2";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";

import { AuthButton } from "./auth/AuthButton";
import { Dashboard } from "./Dashboard";
import { useAuth } from "./auth/AuthProvider";
import makeStyles from "@mui/styles/makeStyles";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";

import { reportRoutes, ReportRoute } from "./reports";

const useStyles = makeStyles(() => ({
  iframe: {
    width: "100%",
    height: "100%",
  },
  image: {
    maxHeight: "100%",
    minWidth: "100%",
    objectFit: "cover",
  },
  content: {
    width: "100%",
    height: "100%",
  },
}));

function getCurrentPath() {
  return window.location.pathname.split("/")[1]?.toLocaleLowerCase();
}

const getCurrentReport = (): ReportRoute => {
  const path = getCurrentPath();
  return reportRoutes.hasOwnProperty(path)
    ? reportRoutes[path]
    : reportRoutes["market-reports"];
};

const useCurrentReport = () => {
  const [report, setReport] = React.useState<ReportRoute>(getCurrentReport());
  const [page, setPage] = React.useState("0");

  React.useEffect(() => {
    const handleLocationChange = () => {
      const report = getCurrentReport();
      setReport(report);
      setPage("0");
      window.document.title = report.title;
    };

    window.addEventListener("popstate", handleLocationChange);
    window.document.title = report.title;
    return () => window.removeEventListener("popstate", handleLocationChange);
  }, []);

  return { report, setPage, page };
};

export const App = () => {
  const { user, userDetails } = useAuth();
  const { report, page } = useCurrentReport();
  const styles = useStyles();

  const [dialogOpen, setDialogOpen] = React.useState(!user);

  React.useEffect(() => {
    setDialogOpen(!user);
  }, [user]);

  const SigninDialog = (
    <Dialog open={dialogOpen}>
      <DialogContent>
        <DialogContentText>
          Please log in to access the reports.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <AuthButton />
      </DialogActions>
    </Dialog>
  );

  return (
    <Grid columns={1} style={{ height: "100vh", overflow: "hidden" }}>
      <AppBar
        position="static"
        style={{
          borderBottom: "1px solid ##0d4c24",
        }}
      >
        <Toolbar style={{ justifyContent: "space-between", minHeight: "64px" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexGrow: 1,
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <Typography
              component="a"
              href="https://www.greenlight-dc.com/unlockmls-datamenu"
              sx={{
                textDecoration: "none",
                marginRight: 2,
                display: "flex",
                alignItems: "center",
                minWidth: "170px",
                color: "white",
              }}
            >
              ← Back to Main Menu
            </Typography>
            <Typography variant="h6">{report.label}</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {userDetails && (
              <Typography>
                {userDetails.firstname} {userDetails.lastname},{" "}
                {userDetails.officeName}
              </Typography>
            )}
            <AuthButton />
          </Box>
        </Toolbar>
        {SigninDialog}
      </AppBar>
      <Grid direction={"column"} style={{ height: `calc(100vh - 64px)` }}>
        {user ? (
          <Dashboard
            url={report.url.replace("[PAGE_ID]", report.pages[+page].value)}
          />
        ) : (
          <Grid direction={"column"} className={styles.content}>
            <picture>
              <source
                srcSet="//images.ctfassets.net/pu0snv8rn9fa/2Wv9jaHRatKNG75gimXiSq/ccaf536b78eebe9290bf6f7dfbe9367b/UnlockMLS-Homepage-Header-1.png?w=3072"
                media="(max-width: 1536px)"
              />
              <source
                srcSet="//images.ctfassets.net/pu0snv8rn9fa/2Wv9jaHRatKNG75gimXiSq/ccaf536b78eebe9290bf6f7dfbe9367b/UnlockMLS-Homepage-Header-1.png?w=4000"
                media="(max-width: 2000px)"
              />
              <img
                className={styles.image}
                src="//images.ctfassets.net/pu0snv8rn9fa/2Wv9jaHRatKNG75gimXiSq/ccaf536b78eebe9290bf6f7dfbe9367b/UnlockMLS-Homepage-Header-1.png?w=4000"
                alt="image"
                loading="eager"
              />
            </picture>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
